import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth } from "@/hooks/useAuth";

interface ForbiddenProps {
  message?: string;
  description?: string;
  showRetry?: boolean;
  showHome?: boolean;
  onRetry?: () => void;
  onGoHome?: () => void;
}

/**
 * Shared Forbidden/Access Denied component
 * Used across the application for consistent error handling
 */
export function Forbidden({ 
  message = "Access Denied",
  description = "You don't have permission to view this content.",
  showRetry = true,
  showHome = true,
  onRetry,
  onGoHome 
}: ForbiddenProps) {
  const { logout } = useAuth();
  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = '/';
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" data-testid="permission-error">
      <Card className="w-full max-w-md mx-auto shadow-lg">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900">{message}</CardTitle>
          <CardDescription className="text-gray-600 mt-2">
            {description}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4 pt-0">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              If you believe this is an error, please contact your administrator or try refreshing the page.
            </p>
          </div>
          
          <div className="flex flex-col gap-2 sm:flex-row">
            {showHome && (
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={handleGoHome}
              >
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            )}
            
            {showRetry && (
              <Button 
                variant="default" 
                className="flex-1" 
                onClick={handleRetry}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
          </div>
          
          <div className="text-center">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="text-gray-500 hover:text-gray-700"
            >
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Pre-configured variants for common scenarios
 */

export function ForbiddenAuth() {
  const { loginWithRedirect } = useAuth0();
  return (
    <Forbidden
      message="Authentication Required"
      description="Please sign in to access this content."
      showRetry={false}
      onGoHome={() => loginWithRedirect()}
    />
  );
}

export function ForbiddenPermission() {
  return (
    <Forbidden
      message="Insufficient Permissions"
      description="You don't have the required permissions to access this resource."
    />
  );
}

export function ForbiddenPlanLimit() {
  return (
    <Forbidden
      message="Plan Limit Reached"
      description="Your current plan doesn't allow access to this feature. Please upgrade your plan."
      showRetry={false}
    />
  );
}